import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { money } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { RegenLogo } from '@/components/RegenLogo';
import { Check, ArrowLeft, Loader2, Star, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PromoBanner } from '@/components/PromoBanner';

const INCLUDED = [
  'Personalized Workout Program', 'Personalized Nutrition Plan', 'Regular Program Updates',
  'In-App Coach Support', 'Progress Tracking', 'Recovery Tracking', 'Full YourReGen+ Premium Access Included',
];

async function detectCountry() {
  try {
    const r = await fetch('https://ipapi.co/json/');
    const j = await r.json();
    return j.country_name || j.country || '';
  } catch { return ''; }
}

export default function EnrollRegenMethod() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [displayCcy, setDisplayCcy] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (dc) => {
    const country = user?.country || (await detectCountry()) || 'US';
    const r = await api.get('/pricing/regen-method', { params: { country, display_currency: dc || undefined } });
    setPricing(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeCurrency = async (ccy) => { setDisplayCcy(ccy); await load(ccy === pricing.currency ? null : ccy); };

  if (!pricing) return <div className="min-h-screen bg-[var(--c-elev2)] flex items-center justify-center text-[var(--c-ink-soft)]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const native = pricing.currency;
  const disp = pricing.display; // present when converted
  const show = (native_amount) => disp
    ? `${disp.currency} ${native_amount === pricing.transform_180.price ? disp.transform_180 : disp.transform_365}`
    : money(pricing.symbol, native_amount);

  const buy = async (program) => {
    const block = program === 'transform_180' ? pricing.transform_180 : pricing.transform_365;
    setLoading(true);
    try {
      const r = await api.post('/payments/checkout', {
        program, plan_type: 'full', currency: native, amount: block.price,
        customer_email: user.email,
      });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; }
      else { toast.success('Enrolled (mock mode)'); await refresh(); nav('/onboarding/questionnaire'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-elev2)] text-[var(--c-ink)] px-6 py-8" data-testid="enroll-regen-method"
         style={{ paddingTop: 'max(env(safe-area-inset-top),2rem)' }}>
      <div className="max-w-md mx-auto">
        <button onClick={() => nav('/choose-journey')} className="text-[var(--c-ink-soft)] hover:text-[var(--c-ink)] inline-flex items-center gap-2 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex items-center gap-2"><RegenLogo size="sm" /><span className="label-mini text-[var(--c-green-ink)]">YourReGen Protocol</span></div>
        <h1 className="font-display font-black text-3xl mt-2 tracking-tight">YourReGen Protocol</h1>
        <p className="text-[var(--c-ink-soft)] text-sm mt-2">Your complete transformation with Coach JP. Personalized coaching to help you lose fat, build muscle and improve your health through training, nutrition and accountability.</p>

        <div className="mt-6"><PromoBanner /></div>

        <div className="mt-6 rounded-2xl border border-[var(--c-hair)] bg-[var(--c-film)] p-5">
          <div className="label-mini text-[#00C8FF] mb-3">Included</div>
          <div className="space-y-2.5">
            {INCLUDED.map(x => (
              <div key={x} className="flex items-center gap-2.5 text-sm">
                <span className="w-5 h-5 rounded-full bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-[var(--c-green-ink)]" strokeWidth={4} /></span>
                {x}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 mb-3">
          <div className="label-mini">Choose Your Program</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="currency-select" className="text-xs text-[var(--c-ink-soft)] inline-flex items-center gap-1 border border-[var(--c-hair)] rounded-lg px-2.5 py-1.5">
                {disp ? disp.currency : native} <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-regen-card border-[var(--c-hair)]">
              {pricing.available_currencies.map(c => (
                <DropdownMenuItem key={c} onClick={() => changeCurrency(c)} data-testid={`ccy-${c}`}>{c}{c === native ? ' (local)' : ''}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {disp && <p className="text-[11px] text-[#FFB800] mb-3">Prices shown in {disp.currency} for reference · {disp.note} {native}.</p>}

        <div className="space-y-4">
          <ProgramCard testid="program-180" title="Transform 180" months="6-Month Transformation" color="#00C8FF"
            promo={pricing.transform_180.promo} regular={money(pricing.symbol, pricing.transform_180.regular)}
            price={show(pricing.transform_180.price)} perMonth={`${money(pricing.symbol, Math.round(pricing.transform_180.price / 6))}/mo`}
            onBuy={() => buy('transform_180')} loading={loading} />
          <ProgramCard testid="program-365" title="Transform 365" months="12-Month Transformation" color='#65A30D' highlight
            promo={pricing.transform_365.promo} regular={money(pricing.symbol, pricing.transform_365.regular)}
            price={show(pricing.transform_365.price)} perMonth={`${money(pricing.symbol, Math.round(pricing.transform_365.price / 12))}/mo`}
            onBuy={() => buy('transform_365')} loading={loading} />
        </div>
        <p className="text-[11px] text-[var(--c-ink-soft)] text-center mt-4">One-time payment · Powered by HitPay · Secure card payments</p>
      </div>
    </div>
  );
}

function ProgramCard({ testid, title, months, color, promo, regular, price, perMonth, highlight, onBuy, loading }) {
  return (
    <div data-testid={testid} className="rounded-2xl border p-5 relative overflow-hidden"
         style={{ borderColor: highlight ? color : `${color}44`, background: highlight ? `${color}0f` : 'rgba(255,255,255,0.03)', boxShadow: highlight ? `0 0 0 1px ${color}55` : 'none' }}>
      {highlight && <span className="absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: color, color: '#04203f' }}><Star className="w-3 h-3" /> Popular</span>}
      <div className="font-display font-black text-xl" style={{ color }}>{title}</div>
      <div className="text-xs text-[var(--c-ink-soft)]">{months}</div>
      <div className="mt-3 flex items-end gap-2">
        {promo && <span className="text-sm text-[var(--c-ink-soft)] line-through">{regular}</span>}
        {promo && <span className="text-[10px] font-bold text-[#FF5C5C] uppercase">Promo</span>}
      </div>
      <div className="font-mono font-black text-3xl mt-0.5">{price}</div>
      <div className="text-xs text-[var(--c-ink-soft)]">≈ {perMonth}</div>
      <button onClick={onBuy} disabled={loading} data-testid={`${testid}-buy`}
              className="w-full h-12 rounded-xl font-bold mt-4 text-[#04203f] flex items-center justify-center disabled:opacity-50" style={{ background: color }}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choose ${title}`}
      </button>
    </div>
  );
}
